// The words the contact card puts on a fact: its status, its sources, a refutation's reason, a site's title.
// Pure — no React — so the card's copy is testable and the page only lays it out. Never a trait word: the
// style site shows the counts the API measured and nothing more (ADR 0005, DEC-2026-09-28-03).
import { formatDate } from '../../../lib/format'
import type { AssertionSource, ContactAssertion, ContactCardCounts, InCommon, RefuteReason, Site, SourceKind } from '../../../types/contacts'

/** The nine sites in the order the card lays them out (the API sends them in this order too). */
export const SITE_ORDER: Site[] = ['identity', 'organisation', 'network', 'history', 'communications', 'why-now', 'style', 'interests', 'connections']

export const siteTitles: Record<Site, string> = {
  identity: 'Identity',
  organisation: 'Organisation',
  network: 'Their network',
  history: 'Past work together',
  communications: 'Every communication',
  'why-now': 'Why now',
  style: 'How they write and decide',
  interests: 'What they told you',
  connections: 'Small connections',
}

/** THE ACTIVATOR FRAME (owner, 2026-10-13: "the layout of the contact card should follow the activator methodology with activator buzzwords and
 *  layout alignment"). The Activator Advantage's four behaviours are the card's four lanes; the person and their organisation are the foundation
 *  above them. Every site keeps its own panel; the lanes only say which behaviour each serves. */
export interface Lane { key: 'commit' | 'connect' | 'create' | 'collaborate'; name: string; line: string; sites: Site[] }
export const LANES: Lane[] = [
  { key: 'commit', name: 'Commit', line: 'Make business development a habit: the cadence, the last touch, the next step.', sites: ['communications'] },
  { key: 'connect', name: 'Connect', line: 'Build the relationship on purpose: who they know, how they write and decide, what you share.', sites: ['network', 'style', 'connections'] },
  { key: 'create', name: 'Create value', line: 'Bring insight before you ask: what moved, what they care about, the industries you can speak to.', sites: ['why-now', 'interests'] },
  { key: 'collaborate', name: 'Collaborate', line: 'Sell as a team: who else at the firm knows them, who introduced whom, what you have done together.', sites: ['history'] },
]
export const FOUNDATION_SITES: Site[] = ['identity', 'organisation']

/** The industries a person's board seats touch, read off the "board-of" facts ("Director, Acme Foods (frozen food)" → frozen food). */
export function industriesOf(values: string[]): string[] {
  const out: string[] = []
  for (const v of values) { const m = /\(([^()]{2,60})\)\s*$/.exec(v); if (m && !out.includes(m[1])) out.push(m[1]) }
  return out
}

/** What the inline form at the foot of a site invites: your read on style, their words on interests. */
export function addLabel(site: Site): string {
  if (site === 'style') return 'Add your read'
  if (site === 'interests') return 'Add what they told you'
  return 'Add what you know'
}

export const sourceWords: Record<SourceKind, string> = {
  'own-mail': 'your mail',
  calendar: 'your calendar',
  onenote: 'your OneNote',
  import: 'an import',
  research: 'research',
  engagement: 'a pursuit on file',
  tie: 'mail both ways',
  said: 'said',
  scout: 'read by Scout',
  crm: 'the CRM',
  document: 'a document',
  vendor: 'a licensed vendor',
}

/** A document source's reference, taken apart: the file, where in it, and the words it stands on (REQ-120).
 *  The API writes it as `<file name>, <where>: "<quote>"`; a reference without the quote is the file and the place. */
export function documentReference(reference: string | null): { file: string; where: string | null; quote: string | null } {
  if (!reference) return { file: 'a document', where: null, quote: null }
  const q = reference.indexOf(': "')
  const head = q >= 0 ? reference.slice(0, q) : reference
  const tail = q >= 0 ? reference.slice(q + 3).replace(/"$/, '') : null
  const comma = head.lastIndexOf(', ')
  const file = comma >= 0 ? head.slice(0, comma) : head
  const where = comma >= 0 ? head.slice(comma + 2) : null
  return { file: file || 'a document', where: where || null, quote: tail || null }
}

export const REASONS: RefuteReason[] = ['wrong-person', 'wrong-company', 'wrong-role', 'not-a-colleague', 'stale', 'private']

export const reasonWords: Record<RefuteReason, string> = {
  'wrong-person': 'wrong person',
  'wrong-company': 'wrong company',
  'wrong-role': 'wrong role',
  'not-a-colleague': 'not a colleague',
  stale: 'out of date',
  private: 'private',
}

/** A reason kind the API sent, in human words; an unknown kind is shown as it came. */
export function reasonWord(kind: string | null): string | null {
  if (!kind) return null
  return (reasonWords as Record<string, string>)[kind] ?? kind
}

/** Five places, the rating filled: 4 → "★★★★☆". Plain characters, not emoji. */
export function stars(rating: number): string {
  const n = Math.max(0, Math.min(5, Math.round(rating)))
  return '★'.repeat(n) + '☆'.repeat(5 - n)
}

/** The status chip's text: "locked in · ★★★★☆", "corroborated · 2 sources", "guessed", "not right · out of date". */
export function statusWords(a: Pick<ContactAssertion, 'status' | 'rating' | 'corroboration' | 'reasonKind'>): string {
  switch (a.status) {
    case 'locked':
      return a.rating ? `locked in · ${stars(a.rating)}` : 'locked in'
    case 'corroborated':
      return `corroborated · ${a.corroboration} source${a.corroboration === 1 ? '' : 's'}`
    case 'refuted': {
      const why = reasonWord(a.reasonKind)
      return why ? `not right · ${why}` : 'not right'
    }
    default:
      return 'guessed'
  }
}

/** One source in words: "your mail, Sep 14, 2026 — Don Smith"; a document is "from <file>, <where>". */
/** A reference that is a page on the web (PL-219): the research cited it; the card shows its host and links it. */
export function pageOf(reference: string | null | undefined): { host: string; url: string } | null {
  if (!reference || !/^https?:\/\//i.test(reference)) return null
  try { return { host: new URL(reference).hostname.replace(/^www\./, ''), url: reference } } catch { return null }
}

export function sourceWord(s: AssertionSource): string {
  const page = pageOf(s.reference)
  if (page) return `a page on ${page.host}${s.at ? `, ${formatDate(s.at)}` : ''}`
  // a licensed vendor (PL-217): the reference names the vendor, its own confidence and when it was refreshed
  if (s.kind === 'vendor') return `a licensed vendor: ${s.reference ?? 'unnamed'}`
  if (s.kind === 'document') {
    const r = documentReference(s.reference)
    return `from ${r.file}${r.where ? `, ${r.where}` : ''}${s.at ? `, ${formatDate(s.at)}` : ''}${s.saidBy ? ` — ${s.saidBy}` : ''}`
  }
  const kind = sourceWords[s.kind] ?? s.kind
  return `${kind}${s.at ? `, ${formatDate(s.at)}` : ''}${s.saidBy ? ` — ${s.saidBy}` : ''}`
}

/** What the source shows on hover: a document's quote; nothing for the rest. */
export function sourceTitle(s: AssertionSource): string | undefined {
  const page = pageOf(s.reference)
  if (page) return page.url
  return s.kind === 'document' ? documentReference(s.reference).quote ?? undefined : undefined
}

/** What stands behind a fact, for the status chip's hover (PL-219): how many independent sources, and how it was reached. */
export function provenanceTitle(a: Pick<ContactAssertion, 'corroboration' | 'hops' | 'status'>): string {
  const sources = `${a.corroboration} independent source${a.corroboration === 1 ? '' : 's'}`
  const reach = a.hops >= 2 ? `; reached in ${a.hops} steps, so it starts as a guess` : ''
  return a.status === 'refuted' ? 'A person said this is not right; it is kept so it is never re-derived.' : `${sources}${reach}`
}

/** The source line under a fact: every source, joined by " · ". */
export function sourceLine(sources: AssertionSource[]): string {
  return sources.map(sourceWord).join(' · ')
}

/** The four count chips in the header, each with the class that colours it. */
export function countChips(c: ContactCardCounts): { status: 'locked' | 'corroborated' | 'guessed' | 'refuted'; text: string }[] {
  return [
    { status: 'locked', text: `${c.locked} locked in` },
    { status: 'corroborated', text: `${c.corroborated} corroborated` },
    { status: 'guessed', text: `${c.guessed} guessed` },
    { status: 'refuted', text: `${c.refuted} not right` },
  ]
}

/** The chip on a "small connection": a coincidence is "in common"; the rest say what was shared. */
export function inCommonWord(kind: InCommon['kind']): string {
  switch (kind) {
    case 'coincidence': return 'in common'
    case 'mention': return 'you both mentioned'
    case 'person': return 'mutual contact'
    case 'meeting': return 'meeting shared'
    case 'note': return 'a note'
  }
}

/** Whether the row offers Withdraw: the caller's own said fact, or their own lock or refutation. The API refuses
 *  a withdrawal that is not the caller's, and the refusal is shown on the row. */
export function withdrawable(a: Pick<ContactAssertion, 'status' | 'authorId' | 'ratedBy'>): boolean {
  if (a.authorId) return true
  return a.ratedBy != null && (a.status === 'locked' || a.status === 'refuted')
}

// THE TRUST GAUGE (Don, stand-up 2026-10-12): the relationship strength read from the rep's own mail, on a ten-point scale, that the
// rep may move only by saying what happened — "the system should ask why … so that it can learn from it". The adjustment is a
// correction on the ledger (kind `relationship`), never an edit of the stored strength; the mail keeps deciding, the loop learns.
export const TRUST_POINTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const

/** The strength (0–1) as the gauge's ten points; null when nothing is read yet. */
export function trustPoints(strength: number | null | undefined): number | null {
  if (strength == null || Number.isNaN(strength)) return null
  return Math.min(10, Math.max(1, Math.round(strength * 10)))
}

/** The correction the card records for an adjustment; null, with the reason, when it is not one the ledger will take. */
export function trustAdjustment(personId: string, strength: number | null | undefined, to: number, why: string):
  { entityKind: 'relationship'; entityId: string; fromValue: string | null; toValue: string; reason: string } | { refused: string } {
  if (!Number.isInteger(to) || to < 1 || to > 10) return { refused: 'Pick where it stands, 1 to 10.' }
  const reason = why.trim()
  if (reason.length < 3) return { refused: 'Say what happened. The reason is what Scout learns from.' }
  const from = trustPoints(strength)
  if (from === to) return { refused: `It already reads ${to}.` }
  return { entityKind: 'relationship', entityId: personId, fromValue: from === null ? null : String(from), toValue: String(to), reason }
}
